import { GraduationCap, SlidersHorizontal, Trash2 } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";

import Competence from "@/models/Competence";
import { useOrganisation } from "@/networking/organisation.hooks";
import { getCompetencesLabels } from "@/utils/competence.utils";

interface CompetenceRowProps {
	/** Index of the skill within `item.skills` (used to resolve the skill label). */
	index: number;
	item: Competence;
	skill?: string;
	onSetupModal: (item: Competence) => void;
	onRemoveSkills: (branch: string, service: string, skill?: string) => void;
}

/**
 * A single added-competence row on the consultant "Kompetens" tab. Mirrors the
 * certifikat-card row styling: a labelled pill (branch › service) with an
 * optional salary-supplement badge, plus small icon actions to edit the
 * supplement or remove the competence/skill.
 */
const CompetenceRow: React.FC<CompetenceRowProps> = ({
	index,
	item,
	skill,
	onSetupModal,
	onRemoveSkills,
}) => {
	const { t } = useTranslation();
	const { data: organisation } = useOrganisation();

	const { branch, service, skills } = getCompetencesLabels(
		organisation?.competences || [],
		item,
		index
	);

	const supplement = item?.supplement && item.supplement > 0 ? item.supplement : 0;

	return (
		<div className="flex items-start justify-between gap-3 border-t border-gray-200 py-3 first:border-t-0 first:pt-0">
			<div className="min-w-0">
				<div className="flex flex-wrap items-center gap-2">
					<span className="inline-flex max-w-full items-center gap-1.5 rounded border border-gray-300 bg-white px-2.5 py-1 text-sm font-medium text-gray-800">
						<GraduationCap size={14} className="shrink-0" />
						<span className="truncate">
							{branch}
							{service ? ` › ${service}` : ""}
							{skills.length > 0 ? ` › ${skills.join(", ")}` : ""}
						</span>
					</span>
					{supplement > 0 && (
						<span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
							+ {supplement} SEK
						</span>
					)}
				</div>
			</div>

			<div className="flex shrink-0 items-center gap-1">
				<button
					type="button"
					onClick={() => onSetupModal(item)}
					title={t("consultant.salary.supplement")}
					aria-label={t("consultant.salary.supplement")}
					className="flex h-8 w-8 items-center justify-center rounded-sm border border-transparent bg-transparent text-gray-400 transition-colors hover:border-gray-200 hover:bg-gray-50 hover:text-gray-700"
				>
					<SlidersHorizontal size={16} />
				</button>
				<button
					type="button"
					onClick={() =>
						skill
							? onRemoveSkills(item?.branch || "", item?.service || "", skill)
							: onRemoveSkills(item?.branch || "", item?.service || "")
					}
					title={t("REMOVE")}
					aria-label={t("REMOVE")}
					className="flex h-8 w-8 items-center justify-center rounded-sm border border-transparent bg-transparent text-gray-400 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600"
				>
					<Trash2 size={16} />
				</button>
			</div>
		</div>
	);
};

export default CompetenceRow;
